import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Palmtree, Pencil, Plus, RefreshCw, Rocket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import QuestionEditor from "@/components/admin/QuestionEditor";

const L = "ABCD";
const err = (e) => toast.error(formatError(e?.response?.data?.detail) || e.message);

export default function HolidayPackAdmin() {
  const [grade, setGrade] = useState(1);
  const [pack, setPack] = useState(null);
  const [subject, setSubject] = useState("Maths");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/holiday/pack/${grade}`).then(({ data }) => setPack(data)).catch(err);
  useEffect(() => { setPack(null); load(); }, [grade]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (pack && !pack.subjects.some((s) => s.subject === subject)) setSubject(pack.subjects[0]?.subject); }, [pack, subject]);

  const act = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (e) { err(e); } finally { setBusy(""); } };
  const regenerate = () => window.confirm(`Rebuild the Grade ${grade} Holiday Pack from the teacher's questions? Your manual changes to this pack will be replaced.`)
    && act("regen", async () => { setPack((await api.post(`/admin/holiday/pack/${grade}/regenerate`)).data); toast.success("Pack rebuilt"); });
  const remove = (i) => act(`rm${i}`, async () => { setPack((await api.post(`/admin/holiday/pack/${grade}/remove`, { subject, i })).data); toast.success("Removed from the pack"); });
  const download = () => act("xlsx", async () => {
    const { data } = await api.get(`/admin/holiday/pack/${grade}/export`, { responseType: "blob" });
    Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: `anou-appran-holiday-pack-grade-${grade}.xlsx` }).click();
  });
  const sec = pack?.subjects.find((s) => s.subject === subject);

  return (
    <div className="min-h-screen bg-[#FFF7ED] font-parent text-slate-900" data-testid="holiday-pack-admin">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-orange-200">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="holiday-pack-back"><ArrowLeft size={16} /> Admin</Link>
          <span className="font-bold flex items-center gap-2"><Palmtree size={18} className="text-orange-500" /> Holiday Pack questions</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex flex-wrap items-center gap-2">
          {[1, 2, 3, 4, 5, 6].map((g) => (
            <button key={g} onClick={() => setGrade(g)} data-testid={`holiday-pack-grade-${g}`}
              className={`rounded-full border-2 border-slate-900 px-4 py-1.5 text-sm font-bold transition-colors ${grade === g ? "bg-orange-500 text-white" : "bg-white hover:bg-orange-50"}`}>Grade {g}</button>
          ))}
          <span className="ml-auto flex gap-2">
            <button onClick={download} disabled={!!busy || !pack} data-testid="holiday-pack-download" className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-900 bg-white px-4 py-1.5 text-sm font-bold disabled:opacity-50">
              {busy === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel</button>
            <button onClick={regenerate} disabled={!!busy || !pack} data-testid="holiday-pack-regenerate" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-1.5 text-sm font-bold disabled:opacity-50">
              {busy === "regen" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rebuild pack</button>
          </span>
        </div>
        {!pack ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-orange-500" /></div> : (
          <>
            <p className="text-sm text-slate-600 mt-4" data-testid="holiday-pack-summary">
              Season {pack.season} · <b>{pack.total}</b> questions · same pack for every Grade {grade} child · children get one question of each subject in turn · medium & difficult questions, at least 5 different notions per chapter, max 1 reading question per chapter
              {pack.edited_at && <> · edited {new Date(pack.edited_at).toLocaleDateString()}</>}
            </p>
            <p className="text-xs text-amber-700 mt-1" data-testid="holiday-pack-tip">Tip: finish your changes before 15 November. Removing or adding questions once children have started can make them skip or repeat one question.</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {pack.subjects.map((s) => (
                <button key={s.subject} onClick={() => setSubject(s.subject)} data-testid={`holiday-pack-subject-${s.subject}`}
                  className={`rounded-full border-2 px-3 py-1 text-sm font-semibold ${subject === s.subject ? "border-orange-500 bg-orange-100" : "border-slate-200 bg-white"}`}>
                  {s.subject} · {s.items.length}</button>
              ))}
            </div>
            {sec && <AddQuestion grade={grade} subject={subject} onAdded={setPack} />}
            <div className="mt-4 space-y-3">
              {sec?.items.map((q) => <PackItem key={`${q.chapter_id}-${q.level}-${q.qi}`} q={q} busy={busy === `rm${q.i}`}
                onEdit={() => setEditing({ chapter_id: q.chapter_id, level: q.level, qi: q.qi })} onRemove={() => remove(q.i)} />)}
            </div>
          </>
        )}
        <QuestionEditor target={editing} onClose={() => setEditing(null)} onSaved={load} />
      </main>
    </div>
  );
}

function PackItem({ q, busy, onEdit, onRemove }) {
  return (
    <div className="rounded-2xl border border-orange-200 bg-white p-4 shadow-sm" data-testid={`holiday-pack-item-${q.i}`}>
      <div className="flex items-start gap-2">
        <span className="text-xs font-bold text-slate-400 w-8 shrink-0 pt-0.5">#{q.i + 1}</span>
        {q.has_image && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/question-image?chapter_id=${encodeURIComponent(q.chapter_id)}&level=${q.level}&qi=${q.qi}&lang=en`} alt=""
          loading="lazy" className="w-24 h-20 object-contain rounded-xl border border-slate-200 bg-slate-50 shrink-0" data-testid={`holiday-pack-img-${q.i}`} />}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500">
            {q.psac && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 font-bold mr-1.5"><Rocket size={11} /> PSAC prep</span>}
            {q.next && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 font-bold mr-1.5"><Rocket size={11} /> Next year · G{q.grade}</span>}
            Ch. {q.chapter_no} · {q.chapter_title}
          </p>
          <p className="font-semibold mt-1" data-testid={`holiday-pack-question-${q.i}`}>{q.question}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {q.options.map((o, k) => (
              <span key={k} className={`rounded-lg border px-2 py-0.5 text-xs ${k === q.answer ? "bg-green-50 border-green-300 text-green-800 font-bold" : "bg-slate-50 border-slate-200 text-slate-600"}`}>{L[k]}. {o}</span>
            ))}
          </div>
          {q.explanation && <p className="text-xs text-slate-500 mt-2">{q.explanation}</p>}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button onClick={onEdit} data-testid={`holiday-pack-edit-${q.i}`} className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-3 py-1 text-xs font-bold"><Pencil size={12} /> Edit</button>
          <button onClick={onRemove} disabled={busy} data-testid={`holiday-pack-remove-${q.i}`} className="inline-flex items-center gap-1 rounded-full border border-rose-400 text-rose-600 px-3 py-1 text-xs font-bold disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Remove</button>
        </div>
      </div>
    </div>
  );
}

function AddQuestion({ grade, subject, onAdded }) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(grade);
  const [chapters, setChapters] = useState([]);
  const [ch, setCh] = useState("");
  const [level, setLevel] = useState(1);
  const [qs, setQs] = useState(null);
  useEffect(() => { setSrc(grade); setOpen(false); }, [grade, subject]);
  useEffect(() => {
    if (!open) return;
    api.get(`/review/chapters/${src}/${encodeURIComponent(subject)}`).then(({ data }) => {
      const list = src === grade ? data.chapters : data.chapters.slice(0, 1);
      setChapters(list); setCh(list[0]?.chapter_id || "");
    }).catch(() => { setChapters([]); setCh(""); });
  }, [open, src, subject, grade]);
  useEffect(() => {
    if (!ch) { setQs(null); return; }
    api.get("/review/level", { params: { grade: src, subject, chapter_id: ch, level } }).then(({ data }) => setQs(data.questions)).catch(() => setQs([]));
  }, [ch, level, src, subject]);
  const add = async (qi) => {
    try { onAdded((await api.post(`/admin/holiday/pack/${grade}/add`, { subject, grade: src, chapter_id: ch, level, qi })).data); toast.success("Added to the pack"); }
    catch (e) { err(e); }
  };
  if (!open) return <button onClick={() => setOpen(true)} data-testid="holiday-pack-add-open" className="mt-4 inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-orange-400 px-4 py-1.5 text-sm font-bold text-orange-700"><Plus size={14} /> Add a {subject} question</button>;
  const sel = "rounded-xl border-2 border-slate-200 px-3 py-1.5 text-sm bg-white";
  return (
    <div className="mt-4 rounded-2xl border-2 border-orange-300 bg-white p-4" data-testid="holiday-pack-add">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={src} onChange={(e) => setSrc(Number(e.target.value))} className={sel} data-testid="holiday-pack-add-source">
          <option value={grade}>Grade {grade} (this year)</option>
          {grade < 6 && <option value={grade + 1}>Grade {grade + 1} first chapter (next year)</option>}
        </select>
        <select value={ch} onChange={(e) => setCh(e.target.value)} className={`${sel} max-w-xs`} data-testid="holiday-pack-add-chapter">
          {chapters.map((c) => <option key={c.chapter_id} value={c.chapter_id}>{c.index + 1}. {c.title}</option>)}
        </select>
        <select value={level} onChange={(e) => setLevel(Number(e.target.value))} className={sel} data-testid="holiday-pack-add-set">
          <option value={1}>Set 1</option><option value={2}>Set 2</option><option value={3}>Set 3</option>
        </select>
        <button onClick={() => setOpen(false)} className="ml-auto text-sm font-semibold text-slate-500" data-testid="holiday-pack-add-close">Close</button>
      </div>
      <div className="mt-3 max-h-80 overflow-y-auto space-y-1.5">
        {qs === null ? <Loader2 className="animate-spin text-orange-500" /> : qs.map((q) => (
          <div key={q.qi} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm" data-testid={`holiday-pack-candidate-${q.qi}`}>
            <span className="flex-1">{q.question} <span className="text-green-700 font-semibold">→ {q.options[q.answer]}</span></span>
            <button onClick={() => add(q.qi)} data-testid={`holiday-pack-add-${q.qi}`} className="inline-flex items-center gap-1 rounded-full bg-orange-500 text-white px-3 py-1 text-xs font-bold"><Plus size={12} /> Add</button>
          </div>
        ))}
      </div>
    </div>
  );
}
